import { Box, MediaObject } from '@zillow/constellation-10';
import {
  ChatAvatar,
  ChatBubbleWithLinkFeatureToggle,
  LowPrioritySystemMessage,
  MESSAGE_STATUS,
  MessageFooter,
  type MessageStatusVariant,
} from '@zg-rentals/satellite-components';
import { useContext } from 'react';
import stringUtils from '../../../utils/stringUtils';
import type { AttachmentsForMessage } from '../../MessageAttachments';
import MessageAttachments from '../../MessageAttachments';
import { AppContext } from '../../../contexts/AppContextProvider';
import { isHotpads } from '@zg-rentals/multibrand-support';
import { useUserWarnings } from '../../../hooks/useUserWarnings';
import {
  emitExternalLinkClickedInExternalLinkWarningModalEvent,
  emitOpenExternalLinkWarningModalEvent,
} from '../../../constants/clickstreamEvents/externalLinkWarningModal';
import StringConstants from '../../../constants/StringConstants';

type Props = {
  attachments: AttachmentsForMessage;
  conversationId: string;
  handleResendMessage(): void;
  isMessageOwner: boolean;
  messageContent: string;
  messageDateMs: string;
  messageId: string;
  senderName: string;
  sendStatus: MessageStatusVariant;
  shouldDisplaySentStatus: boolean;
};

const ListingMessageItem = ({
  attachments,
  conversationId,
  handleResendMessage,
  isMessageOwner,
  messageContent,
  messageDateMs,
  messageId,
  senderName,
  sendStatus,
  shouldDisplaySentStatus,
}: Props): JSX.Element => {
  const { brand } = useContext(AppContext);
  const hasAttachments = attachments && !!attachments.length;
  const allAttachmentsExpired = hasAttachments && attachments.every((a) => a.isAttachmentExpired);
  const isMessageSent = sendStatus === MESSAGE_STATUS.SENT;
  const isMessageContentEmpty = stringUtils.isEmptyString(messageContent);
  const shouldAdjustOpacity = sendStatus === MESSAGE_STATUS.SENDING || sendStatus === MESSAGE_STATUS.FAILED;

  const { externalLinkWarningData } = useUserWarnings();

  return (
    <>
      <MediaObject
        media={!isMessageOwner && <ChatAvatar senderFullName={senderName} type="user" />}
        css={{
          alignItems: 'end',
          display: 'flex',
          flexDirection: isMessageOwner ? 'row-reverse' : 'row',
          gap: 0,
          whiteSpace: 'pre-wrap',
          width: '100%',
        }}
      >
        <Box css={{ opacity: shouldAdjustOpacity ? 0.4 : 1 }}>
          {!isMessageContentEmpty && (
            <ChatBubbleWithLinkFeatureToggle
              variant={isMessageOwner ? 'messageOwner' : 'default'}
              enableClickableLinks
              externalLinkWarningData={externalLinkWarningData}
              brand={brand}
              onImpact={isHotpads(brand) && isMessageOwner}
              senderName={senderName}
              shouldIncludeGutter
              onExternalLinkWarningModalOpen={emitOpenExternalLinkWarningModalEvent}
              onExternalLinkClickInModal={emitExternalLinkClickedInExternalLinkWarningModalEvent}
              enableHistoryIntegration={true}
            >
              {messageContent}
            </ChatBubbleWithLinkFeatureToggle>
          )}

          {hasAttachments && (
            <MessageAttachments
              attachments={attachments}
              conversationId={conversationId}
              isMessageOwner={isMessageOwner}
              isMessageSent={isMessageSent}
              messageId={messageId}
            />
          )}
        </Box>

        <MessageFooter
          isMessageOwner={isMessageOwner}
          messageDateMs={messageDateMs}
          resendMessage={handleResendMessage}
          sendStatus={sendStatus}
          shouldDisplaySentStatus={shouldDisplaySentStatus}
        />
      </MediaObject>

      {hasAttachments && !isMessageOwner && !allAttachmentsExpired && (
        <LowPrioritySystemMessage
          message={
            isHotpads(brand)
              ? StringConstants.ATTACHMENT_SAFETY_WARNING.hotpads
              : StringConstants.ATTACHMENT_SAFETY_WARNING.zillow
          }
          ctaText={StringConstants.ATTACHMENT_SAFETY_WARNING.ctaText}
          url={StringConstants.ATTACHMENT_SAFETY_WARNING.url}
        />
      )}
    </>
  );
};

export default ListingMessageItem;
